/** Data access for spare parts / inventory. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, type Schemas } from "@/api/client";
import { unwrap } from "@/api/problem";
import {
  compactParams,
  type ListState,
  type Paged,
  type PageOf,
  usePagedQuery,
  usePageQuery,
} from "@/shared/lib/paging";

/** Item row. */
export type Item = Schemas["ItemOut"];
/** Item with stock levels. */
export type ItemDetail = Schemas["ItemDetailOut"];
/** Ledger row. */
export type Transaction = Schemas["TransactionOut"];
/** Reorder suggestion. */
export type ReorderSuggestion = Schemas["ReorderSuggestionOut"];
/** Movement payload. */
export type MovementIn = Schemas["MovementIn"];
/** Storage location. */
export type Location = Schemas["LocationOut"];
/** Item category. */
export type Category = Schemas["CategoryOut"];
/** Warehouse stock row. */
export type WarehouseStockRow = Schemas["WarehouseStockRowOut"];
/** Warehouse operation trace and approval lifecycle. */
export type WarehouseOperation = Schemas["WarehouseOperationOut"];

/** Item list filters. */
export interface ItemFilters {
  q?: string;
  below_min?: boolean;
  kind?: string;
  category_id?: string;
  include_inactive?: boolean;
}

/** Paged items. */
export function useItems(filters: ItemFilters): Paged<Item> {
  return usePagedQuery(["items", filters], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/inventory/items", {
        params: { query: compactParams({ ...filters, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Search catalogue pages on demand instead of downloading the whole catalogue. */
export function useWarehouseItems(q: string, enabled: boolean): Paged<Item> {
  return usePagedQuery(
    ["items", "warehouse-selectors", q],
    async (cursor) =>
      unwrap(
        await api.GET("/api/v1/inventory/items", {
          params: { query: compactParams({ q, cursor, limit: 25 }) },
        }),
      ),
    { enabled },
  );
}

/** Sorted, numbered page of items. */
export function useItemPage(filters: ItemFilters, state: ListState): PageOf<Item> {
  return usePageQuery(["items", "page", filters], state, async (params) =>
    unwrap(
      await api.GET("/api/v1/inventory/items", {
        params: { query: compactParams({ ...filters, ...params }) },
      }),
    ),
  );
}

/** Item categories (for filters and forms). */
export function useCategories(): UseQueryResult<Category[]> {
  return useQuery({
    queryKey: ["item-categories"],
    queryFn: async () => unwrap(await api.GET("/api/v1/inventory/categories")),
    staleTime: 5 * 60_000,
  });
}

/** Single item with stock. */
export function useItem(id: string, enabled = true): UseQueryResult<ItemDetail> {
  return useQuery({
    queryKey: ["items", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/inventory/items/{item_id}", { params: { path: { item_id: id } } }),
      ),
    enabled: enabled && !!id,
  });
}

/** Ledger for an item. */
export function useItemTransactions(
  itemId: string,
  filters: { type?: string; since?: string; until?: string } = {},
): Paged<Transaction> {
  return usePagedQuery(["transactions", { itemId, ...filters }], async (cursor) =>
    unwrap(
      await api.GET("/api/v1/inventory/transactions", {
        params: { query: compactParams({ ...filters, item_id: itemId, cursor, limit: 50 }) },
      }),
    ),
  );
}

/** Reorder suggestions. */
export function useReorderSuggestions(enabled: boolean): UseQueryResult<ReorderSuggestion[]> {
  return useQuery({
    queryKey: ["reorder-suggestions"],
    queryFn: async () => unwrap(await api.GET("/api/v1/inventory/reorder/suggestions")),
    enabled,
  });
}

/** Storage-capable locations for movement forms. */
export function useLocations(): UseQueryResult<Location[]> {
  return useQuery({
    queryKey: ["locations"],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/locations/locations", { params: { query: { stock_only: true } } }),
      ),
    staleTime: 5 * 60_000,
  });
}

/** Numbered, server-filtered stock in a location tree or all accessible locations. */
export function useWarehouseStock(
  filters: { location_id?: string; item_id?: string; q?: string },
  state: ListState,
  enabled = true,
): PageOf<WarehouseStockRow> {
  return usePageQuery(
    ["warehouse", "stock", filters],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/inventory/warehouse/stock", {
          params: {
            query: {
              ...compactParams({ ...filters, ...params }),
              location_id: filters.location_id ?? "all",
            },
          },
        }),
      ),
    { enabled },
  );
}

/** Search the single immutable inventory ledger. */
export function useWarehouseLedger(
  filters: {
    q?: string;
    item_id?: string;
    location_id?: string;
    movement_type?: string;
    date_from?: string;
    date_to?: string;
  },
  state: ListState,
  enabled = true,
): PageOf<Transaction> {
  return usePageQuery(
    ["warehouse", "ledger", filters],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/inventory/warehouse/ledger", {
          params: {
            query: compactParams({ ...filters, ...params }),
          },
        }),
      ),
    { enabled },
  );
}

/** Visible transfers, adjustments and stocktake sessions. */
export function useWarehouseOperations(
  filters: {
    location_id?: string;
    item_id?: string;
    kind?: string;
    status?: string;
    needs_my_action?: boolean;
    q?: string;
  },
  state: ListState,
  enabled = true,
): PageOf<WarehouseOperation> {
  return usePageQuery(
    ["warehouse", "operations", filters],
    state,
    async (params) =>
      unwrap(
        await api.GET("/api/v1/inventory/warehouse/operations", {
          params: { query: compactParams({ ...filters, ...params }) },
        }),
      ),
    { enabled },
  );
}

/** Load one warehouse operation by its detail trace id. */
export function useWarehouseOperation(id: string | undefined): UseQueryResult<WarehouseOperation> {
  return useQuery({
    queryKey: ["warehouse", "operation", id],
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/inventory/warehouse/operations/{operation_id}", {
          params: { path: { operation_id: id ?? "" } },
        }),
      ),
    enabled: !!id,
  });
}

/** Immediately post an idempotent transfer batch. */
export function useWarehouseTransfer(): UseMutationResult<
  WarehouseOperation,
  Error,
  Schemas["WarehouseTransferIn"]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.POST("/api/v1/inventory/warehouse/transfers", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

/** Save a draft adjustment without changing the stock projection. */
export function useProposeWarehouseAdjustment(): UseMutationResult<
  WarehouseOperation,
  Error,
  Schemas["WarehouseAdjustmentIn"]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.POST("/api/v1/inventory/warehouse/adjustments", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Start a blind count session for selected items. */
export function useCreateWarehouseCount(): UseMutationResult<
  WarehouseOperation,
  Error,
  Schemas["WarehouseCountSessionIn"]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap(await api.POST("/api/v1/inventory/warehouse/counts", { body })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Save one count, including zero, in a draft session. */
export function useRecordWarehouseCount(): UseMutationResult<
  WarehouseOperation,
  Error,
  { operationId: string; lineId: string; counted_quantity: number; explanation: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ operationId, lineId, ...body }) =>
      unwrap(
        await api.PUT("/api/v1/inventory/warehouse/operations/{operation_id}/lines/{line_id}", {
          params: { path: { operation_id: operationId, line_id: lineId } },
          body,
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Submit an adjustment/count for independent review. */
export function useSubmitWarehouseOperation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/inventory/warehouse/operations/{operation_id}/submit", {
          params: { path: { operation_id: id } },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Independently approve an adjustment/count. */
export function useApproveWarehouseOperation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/inventory/warehouse/operations/{operation_id}/approve", {
          params: { path: { operation_id: id } },
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Post approved quantities to the single inventory ledger. */
export function usePostWarehouseOperation(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      unwrap(
        await api.POST("/api/v1/inventory/warehouse/operations/{operation_id}/post", {
          params: { path: { operation_id: id } },
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse"] });
      void qc.invalidateQueries({ queryKey: ["items"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
}

/** Return, reject or cancel a warehouse proposal with a retained decision reason. */
export function useDecideWarehouseOperation(): UseMutationResult<
  WarehouseOperation,
  Error,
  { id: string; body: Schemas["WarehouseDecisionIn"] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.POST("/api/v1/inventory/warehouse/operations/{operation_id}/decision", {
          params: { path: { operation_id: id } },
          body,
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Correct an owned draft adjustment before resubmitting for independent review. */
export function useEditWarehouseAdjustment(): UseMutationResult<
  WarehouseOperation,
  Error,
  { id: string; body: Schemas["WarehouseAdjustmentEditIn"] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) =>
      unwrap(
        await api.PATCH("/api/v1/inventory/warehouse/operations/{operation_id}/adjustment", {
          params: { path: { operation_id: id } },
          body,
        }),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["warehouse"] }),
  });
}

/** Download a blind count sheet or posted count results through the API client. */
export async function downloadWarehouseCount(operationId: string, results: boolean): Promise<void> {
  const response = await api.GET(
    results
      ? "/api/v1/inventory/warehouse/operations/{operation_id}/count-results"
      : "/api/v1/inventory/warehouse/operations/{operation_id}/count-sheet",
    {
      params: { path: { operation_id: operationId } },
      parseAs: "blob",
    },
  );
  const blob = unwrap(response);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `stocktake-${results ? "results" : "sheet"}.csv`;
  document.body.append(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

/** Receive stock. */
export function useReceiveStock(): UseMutationResult<Transaction, Error, MovementIn> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) =>
      unwrap<Transaction>(await api.POST("/api/v1/inventory/receipts", { body })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["items"] });
      void qc.invalidateQueries({ queryKey: ["transactions"] });
      void qc.invalidateQueries({ queryKey: ["reorder-suggestions"] });
    },
  });
}
